<script setup lang="ts">
import { computed, reactive, Ref, ref, watch } from 'vue';
import { GradientStopObject, useColor } from '@/composables/color';
import { useClickOutside } from '@/composables/clickOutside';
import CButton from '@/components/shared/CButton';
import FontSelector from './FontSelector.vue';
import ColorSelector from './ColorSelector.vue';
import CInput from '@/components/shared/CInput.vue';
import DraggableList from './DraggableList.vue';
import CPreview from '../shared/CPreview.vue';
import CFileInput, { type CFileInputRef }  from '../shared/CFileInput';
import CSelect, { type CSelectItem, valueReducer } from '@/components/shared/CSelect';
import type { SortableEvent } from 'sortablejs';
import type { ColorObject, GradientObject, LibraryDetail, LibraryDetailElement } from '@/typings/libraries';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ElementMapOption } from './types';
import type { DynamicImage, FontColorOption } from '@/typings/customilyElements/canvasElements';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { useFonts } from '@/composables/fonts';
import { getLibraryDetails, getLibraryElementDetails } from '@/api/libraries';
import fontManagerService from '@/utils/fontManagerService';
import { IMAGE_FILE_EXTENSIONS, VECTOR_FILE_EXTENSIONS } from '@/utils/pathUtils';

const libSearchOptions: CSelectItem<boolean>[] = [
    {
        label: 'Name',
        value: true
    },
    {
        label: 'Option',
        value: false
    }
];

const props = withDefaults(defineProps<{
    libraryId?: number;
    libraryType: number;
    elementList: Map<number, ElementMapOption>;
    selectedPosition?: number;
    cmyk?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    enablePalette?: boolean;
    showMenuTypes?: boolean;
    hideLibrarySelection?: boolean;
    mixedLibrary?: boolean;
    mixedElementList?: boolean;
    elementListPosition?: 'unset' | number;
    readonlyOptions?: boolean;
}>(), {
    elementListPosition: 'unset'
});

const emit = defineEmits<{
    (e: 'pick-library'): void;
    (e: 'remove-library'): void;
    (e: 'remove-map-option', position: number): void;
    (e: 'load-option', opt: number, value?: ElementMapOption): void;
    (e: 'option-selected', value: ElementMapOption, opt: number): void;
    (e: 'options-reordered', value: KeyValueDto<number, ElementMapOption>[]): void;
    (e: 'files-uploaded', value: File[], position?: number): void;
    (e: 'bulk-change-lib-option', value: number): void;
    (e: 'preview-selection', value: ElementMapOption | undefined): void;
    (e: 'library-loaded', value: LibraryDetail): void;
}>();

const params = computed(() => {
    switch (props.libraryType) {
        case LibraryTypeEnum.Color:
            return {
                isColorLib: true,
                wording: 'color'
            };
        case LibraryTypeEnum.Vector:
            return {
                isVectorLib: true,
                wording: 'vector',
                fileTypes: [...VECTOR_FILE_EXTENSIONS].join(','),
                editIcon: 'arrow-up-from-bracket'
            };
        case LibraryTypeEnum.Image:
            return {
                isImageLib: true,
                wording: 'image',
                fileTypes: [...IMAGE_FILE_EXTENSIONS].join(','),
                editIcon: 'arrow-up-from-bracket'
            };
        default:
            return {
                isFontLib: true,
                wording: 'font'
            };
    }
});

const fonts = useFonts();

const selectedFont: Ref<CSelectItem<string> | undefined> = ref();
const optionsMap: Ref<KeyValueDto<number, ElementMapOption>[]> = ref([]);

const selectionMode = reactive({
    open: false,
    positionToEdit: undefined as number | undefined
});

const { hexToHexa, getGradientCSS } = useColor();

const getElementName = (value: ElementMapOption) => {
    let name = '';

    if (!value) return name;

    if (params.value.isFontLib) {
        const font = fontManagerService.getFontFromPath(value as string)!;
        name = fonts.generateFontName(font);
    } else if (params.value.isColorLib) {
        selectionMode.open = true;
        const color = value as ColorObject | GradientObject;
        if ('colorStops' in color) {
            name = color.type === 'linear' ? 'Linear Gradient' : 'Radial Gradient';
        } else if ('colors' in color) {
            name = 'Alternating Colors';
        } else {
            name = hexToHexa(color.hex, color.alpha);
        }
    } else if (params.value.isImageLib) {
        name = (value as DynamicImage).fileName || '';
    }
    return name;
};

const getElementPreview = (value: ElementMapOption) => {
    if (params.value.isImageLib) {
        return (value as DynamicImage).path;
    }
    return value;
};

const getColorElement = (color: unknown): Record<string, string> => {
    if (!color || typeof color !== 'object') return {};
    if ('value' in color) {
        color = color.value;
    }
    const newColor = color as FontColorOption;
    if ('colorStops' in newColor) {
        return getGradientCSS(newColor.colorStops as Pick<GradientStopObject, 'hex' | 'offset'>[], newColor.angle || 0);
    } else if ('colors' in newColor) {
        return {
            backgroundColor: hexToHexa(newColor.colors[0].hex, newColor.colors[0].alpha)
        };
    }
    return {
        backgroundColor: hexToHexa(newColor.hex, newColor.alpha)
    };
};

watch(
    () => props.elementList,
    (elementList) => {
        optionsMap.value = Array.from(elementList).map(([_key, value]) => {
            return {
                id: _key,
                value,
                name: getElementName(value)
            };
        });

        if (optionsMap.value.length != 1) return;

        selectionMode.open = false;
        const option = optionsMap.value[0];

        if (params.value.isFontLib) {
            selectionMode.positionToEdit = option.id;
            selectedFont.value = {
                label: option.name!,
                value: option.value! as string
            };
        }
    },
    { deep: true, immediate: true }
);

const colorElementToEdit = computed(() =>
    params.value.isColorLib && selectionMode.positionToEdit && props.elementList
        ? props.elementList.get(selectionMode.positionToEdit) as ColorObject | GradientObject
        : undefined
);

const displayFontSelector = computed(() => params.value.isFontLib && (selectionMode.open || optionsMap.value.length == 1));

const displayOptionMapList = computed(() =>
    optionsMap.value.length > 1 || selectionMode.open || !params.value.isFontLib
);

const library: Ref<LibraryDetail | undefined> = ref();
const cachedLibs: Map<number, LibraryDetail> = new Map();
const selectedLibCategory: Ref<CSelectItem<number> | undefined> = ref();
const libraryCategories: Ref<CSelectItem<number>[]> = ref([]);

const getLibrary = async (libraryId: number) => {
    const response = cachedLibs.get(libraryId) || (await getLibraryDetails(libraryId));
    library.value = { ...response };
    cachedLibs.set(libraryId, response);
    if (response.categories?.length) {
        library.value.elements = [];
        libraryCategories.value = response.categories.map<CSelectItem<number>>((category) => ({
            value: category.id!,
            label: category.name!
        }));
        selectedLibCategory.value = libraryCategories.value[0];
        await onCategorySelected();
    } else {
        libraryCategories.value = [];
        selectedLibCategory.value = undefined;
    }

    if (library.value.elements.length && props.selectedPosition == 1) {
        onLibraryElementSelected(library.value.elements[0]);
    }
    emit('library-loaded', library.value);
};

const onCategorySelected = async () => {
    await getLibraryElements(selectedLibCategory.value?.value)
}

const searchString = ref('');
const searchByName = ref(true);
const onElementSearch = async () => {
    if (!searchString.value?.toString().trim().length) {
        await onCategorySelected();
        return;
    }

    await getLibraryElements();
}

const resetSearch = async () => {
    searchString.value = '';
    await onCategorySelected();
}

const getLibraryElements = async (categoryId?: number) => {
    if (!library.value) return;

    let position: number | undefined;
    let searchValue: string | undefined = searchString.value;

    if (!searchByName.value && searchString.value.toString().length) {
        searchValue = undefined;
        position = Number(searchString.value);
    }

    const elements = await getLibraryElementDetails(
        library.value.id,
        library.value.type,
        categoryId,
        searchValue,
        position
    );
    library.value.elements = elements;
};

const onLibraryElementSelected = (element: LibraryDetailElement) => {
    const value: ElementMapOption = !params.value.isColorLib
        ? element.path!
        : {
            hex: element.hex || '',
            alpha: element.alpha || 1,
            cmyk: ''
        };
    changeOption(element.position, value);

    if (libraryCategories.value.length && element.categoryId != selectedLibCategory.value?.value) {
        selectedLibCategory.value = libraryCategories.value.find((category) => category.value == element.categoryId);
        onCategorySelected();
    }
};

const onRemoveLibrary = () => {
    libraryCategories.value = [];
    selectedLibCategory.value = undefined;
    emit('remove-library');
};

const openElementSelection = (position?: number) => {
    if (props.disabled || props.readonly || props.readonlyOptions) return;

    selectionMode.open = true;
    selectionMode.positionToEdit = position;

    if (params.value.isImageLib ||params.value.isVectorLib) {
        fileInputRef.value?.openFileUploader();
        return;
    }

    clickOutsideActive.value = true;
};

const closeSelection = () => {
    if (!selectionMode.open) return;
    selectionMode.open = false;
    selectionMode.positionToEdit = undefined;
    clickOutsideActive.value = false;
    emit('preview-selection', undefined);
};

const pickerRef: Ref<HTMLElement | undefined> = ref();
const { clickOutsideActive } = useClickOutside(pickerRef, closeSelection, false);

const onElementSelected = (element?: ElementMapOption) => {
    if (props.mixedElementList && element) {
        emitOptionSelection(1, element)
        closeSelection();
        return;
    }

    if (optionsMap.value.length <= 1 && !selectionMode.open && selectionMode.positionToEdit !== undefined) {
        emitOptionSelection(1);
        return;
    }

    const lastPosition = optionsMap.value.at(-1)?.id || 0;
    const position = selectionMode.positionToEdit || lastPosition + 1;
    closeSelection();
    emitOptionSelection(position, element);
};

const emitOptionSelection = (position: number, value?: ElementMapOption) => {
    value ||= selectedFont.value?.value;
    if (!value) return;

    emit('option-selected', value, position);
};

const onMapOptionRemoved = (position: number, index: number) => {
    if (index == optionsMap.value.length - 1) {
        emit('remove-map-option', position);
        return;
    }

    optionsMap.value.splice(index, 1);
    emit('options-reordered', optionsMap.value);
};

const onOptionDragEnd = (event: SortableEvent) => {
    const { oldIndex = 0, newIndex = 0 } = event;

    if (oldIndex == newIndex) return;
    emit('options-reordered', optionsMap.value);
};

const generateSafeFontName = fontManagerService.generateSafeFontName;

watch(
    () => props.libraryId,
    (value, oldValue) => {
        if (value != library.value?.id) {
            library.value = undefined;
        }
        if (!value || value == oldValue) return;

        getLibrary(value);
    },
    { immediate: true }
);

const changeOption = async (position: string | number, value?: ElementMapOption) => {
    emit('load-option', position as number, value);
};

const fileInputRef: Ref<CFileInputRef | undefined> = ref();
const onFileUpload = (files: File[]) => {
    if (!files.length) return;
    emit('files-uploaded', files, selectionMode.positionToEdit);
};
</script>

<template>
    <div class="element-library-selector-wrapper">
        <section v-if="library || mixedLibrary" class="library-details">
            <div class="header-container">
                <slot name="header" />
                <div class="title rounded d-flex align-items-center align-self-end">
                    <font-awesome-icon :icon="['far', 'book']" class="mr-2" />
                    {{ mixedLibrary ? 'Mixed' : library?.name }}
                    <c-button
                        v-show="!readonly"
                        class="ml-auto"
                        :icon="['far', 'xmark']"
                        size="icon"
                        :disabled="disabled"
                        @click="onRemoveLibrary"
                    />
                </div>
            </div>
            <c-input
                v-if="!library && mixedLibrary"
                :value="selectedPosition"
                label="Option"
                type="number"
                :min="0"
                placeholder="Mixed"
                @input="changeOption"
            />
            <template v-else-if="library">
                <div class="row">
                    <c-select
                        v-if="libraryCategories.length"
                        v-model="selectedLibCategory"
                        class="col-6 pr-0"
                        label="Category"
                        searchable
                        :options="libraryCategories"
                        :disabled="searchString.length > 0"
                        @input="onCategorySelected"
                    />
                    <c-input
                        v-model="searchString"
                        class="col-6 search-box"
                        :class="{ 'col-12': !libraryCategories.length }"
                        placeholder="Search"
                        :type="searchByName ? 'search' : 'number'"
                        @debounce="onElementSearch"
                    >
                        <template #append>
                            <c-select
                                v-model="searchByName"
                                class="type-selector"
                                :options="libSearchOptions"
                                :model-transform="valueReducer"
                                @input="resetSearch"
                            />
                        </template>
                    </c-input>
                </div>
                <draggable-list
                    v-model="library.elements"
                    item-key="position"
                    :draggable="false"
                    :selected="selectedPosition"
                    @row-click="onLibraryElementSelected"
                >
                    <template #item="{ element }">
                        <span v-if="params.isColorLib" class="color-preview" :style="getColorElement(element)" />
                        <c-preview
                            v-if="params.isImageLib || params.isVectorLib"
                            class="mx-2"
                            :src="element.thumbnailPath"
                            :width="30"
                            max-height="30px"
                        />
                        <span :style="{ fontFamily: `'${generateSafeFontName(element.name)}', Arial` }">
                            {{ element.name || element.hex }}
                        </span>
                        <span class="option-label"> Option ID: {{ element.position }} </span>
                    </template>
                </draggable-list>
            </template>
        </section>
        <template v-else>
            <slot name="header" />
            <draggable-list
                v-if="displayOptionMapList && !mixedElementList"
                v-model="optionsMap"
                item-key="id"
                :selected="selectedPosition"
                :draggable="!disabled && !readonly && !readonlyOptions"
                @row-click="(option) => changeOption(option.id)"
                @drag-end="onOptionDragEnd"
            >
                <template #item="{ element, selected, index }">
                    <span
                        v-if="params.isColorLib"
                        class="color-preview"
                        :style="getColorElement(element)"
                        @click.stop="openElementSelection(element.id)"
                    />
                    <span @click.stop="openElementSelection(element.id)">
                        <c-preview
                            v-if="params.isImageLib || params.isVectorLib"
                            class="mx-2"
                            :src="getElementPreview(element.value)"
                            :width="30"
                            max-height="30px"
                        />
                    </span>
                    <span
                        v-if="element.name.length"
                        :style="{ fontFamily: `'${generateSafeFontName(element.name)}', Arial` }"
                    >
                        {{ element.name }}
                    </span>
                    <div v-if="!readonly && !readonlyOptions" class="actions ml-auto" :class="{ visible: selected }">
                        <c-button
                            :icon="['far', params.editIcon || 'pen']"
                            size="icon"
                            :disabled="disabled"
                            @click.stop="openElementSelection(element.id)"
                        />
                        <c-button
                            v-if="optionsMap.length > 1"
                            :icon="['far', 'trash-can']"
                            size="icon"
                            :disabled="disabled"
                            @click.stop="onMapOptionRemoved(element.id, index)"
                        />
                    </div>
                </template>
            </draggable-list>
            <c-file-input
                v-if="params.isImageLib || params.isVectorLib"
                ref="fileInputRef"
                class="d-none"
                :value="[]"
                :accept="params.fileTypes"
                multiple
                :display-preview="false"
                @input="onFileUpload"
                @cancel="closeSelection"
            />
            <font-selector
                v-if="displayFontSelector"
                ref="pickerRef"
                v-model="selectedFont"
                :open="selectionMode.open"
                :disabled="disabled"
                @input="onElementSelected()"
            />
            <color-selector
                v-if="params.isColorLib"
                ref="pickerRef"
                :open="selectionMode.open && clickOutsideActive"
                :is-cmyk="cmyk"
                :color="colorElementToEdit"
                :enable-palette="enablePalette"
                :show-menu-types="showMenuTypes"
                @cancel="closeSelection"
                @color-selected="onElementSelected"
                @color-changed="emit('preview-selection', $event)"
            />
            <div class="actions-container">
                <slot name="actions" />
                <c-input
                    v-if="mixedElementList"
                    :value="selectedPosition"
                    label="Option"
                    type="number"
                    :min="1"
                    placeholder="Mixed"
                    @input="changeOption"
                />
                <template v-if="!readonly && !mixedElementList">
                    <c-button
                        v-if="!readonlyOptions"
                        :class="{ 'flex-fill': !hideLibrarySelection, 'px-5': hideLibrarySelection }"
                        variant="secondary"
                        :text="`Add ${params.wording} option`"
                        :icon="['far', 'plus']"
                        :disabled="disabled"
                        @click="openElementSelection()"
                    />
                    <c-button
                        v-if="!hideLibrarySelection"
                        class="flex-fill"
                        variant="light"
                        text="Pick Library"
                        :icon="['far', 'book']"
                        :disabled="disabled"
                        @click="emit('pick-library')"
                    />
                </template>
            </div>
            <template v-if="mixedElementList">
                <div class="mixed-elements-row">
                    <span>Mixed {{ params.wording }}s</span>
                    <c-button class="py-0" text="Replace" variant="link" size="sm" @click="openElementSelection()" />
                </div>
            </template>
        </template>
    </div>
</template>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.layout {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}

.element-library-selector-wrapper {
    @extend .layout;
    .color-selector-wrapper {
        margin-bottom: -0.625rem;
    }
    .color-preview {
        width: 20px;
        height: 17px;
        border-radius: 2px;
        margin-right: 0.5rem;
    }

    .actions-container {
        @extend .layout;
        flex-direction: row;
        align-items: flex-end;
        & > .c-input-wrapper:not(.no-fill) {
            flex: 1;
        }
    }
    .library-details {
        @extend .layout;
        margin-bottom: 0.625rem;
        .title {
            padding: 0.25rem 1rem;
            border: 1px solid get-color('cyan-light');
            color: get-color('cyan-dark');
        }
        .header-container {
            @extend .layout;
            flex-direction: row;
            & > *:not(.no-fill) {
                flex: 1;
            }
        }
        .option-label {
            margin-left: auto;
            color: get-color('gray');
            font-size: 0.75rem;
        }
        :deep() .search-box {
            display: flex;
            align-items: flex-end;
            .input-group .input-group-append {
                border-right: none !important;
                border-top: none !important;
                border-bottom: none !important;
                .vs__dropdown-toggle {
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                }
            }
        }
    }
    .item-row {
        .actions {
            visibility: hidden;
        }
        &:hover {
            .actions {
                visibility: visible;
            }
        }
    }
    .mixed-elements-row {
        display: flex;
        align-items: center;
        border: 1px solid get-color('gray-light');
        border-radius: 0.25rem;
        padding: 0.625rem;
        font-size: 0.875rem;
        order: v-bind(elementListPosition);
    }
    .draggable-list-wrapper {
        order: v-bind(elementListPosition);
    }
}
</style>
